import { twMerge } from "tailwind-merge";
import { Button } from "@themes/pollinator/ui/Button";
import { ThemeType } from "@core/types/style.types";
import { CountBadge } from "@themes/pollinator/ui/CountBadge";

export type ButtonGroupItem = {
  /** Button label */
  label: string;
  /** Icon component */
  iconComponent?: React.ComponentType<{ className?: string }>;
  /** Click handler */
  onClick: () => void;
  /** Whether this button is active */
  isActive?: boolean;
  /** Badge count to display */
  badgeCount?: number;
};

export type Props = {
  /** Array of button configurations */
  buttons: ButtonGroupItem[];
  /** Layout variant */
  layout?: "default" | "tabs";
  /** Theme variant */
  theme?: ThemeType;
  /** Whether to show shadow on active tab */
  isWithShadow?: boolean;
  /** Additional CSS classes */
  className?: string;
};

/**
 * A group of buttons that can function as tabs or segmented controls.
 */
export function ButtonGroup({
  buttons,
  layout = "default",
  theme = "light",
  isWithShadow = true,
  className,
}: Props) {
  return (
    <div
      className={twMerge(
        "flex w-full items-center justify-center md:w-auto",
        layout === "tabs" && "z-10",
        className
      )}
      data-testid="button-group"
    >
      {buttons.map((button, index) => {
        const buttonElement = (
          <Button
            key={index}
            variant="secondary"
            className={twMerge(
              "border-brand-800",
              theme === "dark" && "border-brand-50",
              index === 0 && "mr-0 rounded-none rounded-l-md border-r-0",
              index === 0 && button.isActive && "focus:border-r-1",
              index === 0 &&
                button.isActive &&
                buttons.length === 2 &&
                "border-r-1",
              index > 0 && index < buttons.length - 1 && "mx-0 rounded-none",
              index === buttons.length - 1 &&
                "ml-0 rounded-none rounded-r-md border-l-0",
              index === buttons.length - 1 &&
                button.isActive &&
                "focus:border-l-1",
              index === buttons.length - 1 &&
                button.isActive &&
                buttons.length === 2 &&
                "border-l-1",
              button.isActive && "bg-brand-100 z-20",
              layout === "tabs" &&
                "rounded-b-none bg-brand-100 text-brand-700 hover:bg-brand-200",
              layout === "tabs" &&
                theme === "dark" &&
                "bg-brand-800 text-brand-50 hover:bg-brand-700",
              button.isActive &&
                layout === "tabs" &&
                "border-b-transparent bg-brand-50 text-brand-800 hover:bg-brand-50",
              button.isActive &&
                layout === "tabs" &&
                isWithShadow &&
                "md:shadow-[0_8px_6px_-1px_#fdfdc1,_0_8px_4px_-2px_#fdfdc1]",
              button.isActive &&
                layout === "tabs" &&
                theme === "dark" &&
                "border-b-transparent bg-brand-900 text-brand-50 hover:bg-brand-700",
              button.isActive &&
                layout === "tabs" &&
                theme === "dark" &&
                isWithShadow &&
                "md:shadow-[0_8px_6px_-1px_#0b1400,_0_8px_4px_-2px_#0b1400]"
            )}
            iconComponent={button.iconComponent}
            onClick={button.onClick}
            theme={theme}
            data-testid={`button-group-item-${index}`}
          >
            {button.label}
          </Button>
        );

        if (button.badgeCount && button.badgeCount > 0) {
          return (
            <div key={index} className="relative flex items-center">
              {buttonElement}
              <CountBadge className="z-30" count={button.badgeCount} />
            </div>
          );
        }

        return buttonElement;
      })}
    </div>
  );
}

export default ButtonGroup;
